/**
 * WH-RESTOCK-01 (Seth 2026-09-29): the SHELF count rotation — "it feels like my
 * GM forgets after a couple months." The remembering moves out of the GM's
 * head: this always shows whichever tracked items are most overdue (never-
 * counted first). Save writes history (pegasus_stock_count), updates on-hand
 * (item.qty_in_stock) and restamps the rotation. Opt-in per item — honest
 * counts only (the REFRIG-STOCK-COUNT lesson).
 *
 * v2 (DESC-CLEAN-01): words moved to the CountsHub InfoTip; `embedded` renders
 * just the panel inside the Cycle Counts hub (chips: Warehouse · each truck).
 */
import { useState } from "react";
import { ClipboardCheck, Check, X, Plus, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { PricebookPicker } from "@/features/items/PricebookPicker";
import { useCountDue, useSaveCounts, useTrackItem, type CountDueItem } from "./restock";

const qtyText = (n: number) => (Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));
const dayText = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function CycleCountCard({ embedded = false }: { embedded?: boolean }) {
  const { data, isLoading } = useCountDue(5);
  const save = useSaveCounts();
  const track = useTrackItem();
  const [vals, setVals] = useState<Record<string, string>>({});
  const [picking, setPicking] = useState(false);
  const [arm, setArm] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const due = data?.due ?? [];
  const tracked = data?.tracked ?? 0;

  const parsed = (d: CountDueItem): number | null => {
    const raw = (vals[d.id] ?? "").trim();
    if (raw === "") return null;
    const n = Number(raw);
    return Number.isNaN(n) || n < 0 ? null : n;
  };
  const filled = due.filter((d) => parsed(d) != null);

  const saveAll = async () => {
    const rows = filled.map((d) => ({ item: d, counted: parsed(d) as number }));
    if (rows.length === 0) return;
    await save.mutateAsync(rows);
    setVals({});
    setArm(null);
    setFlash(`Saved ${rows.length}.`);
    window.setTimeout(() => setFlash(null), 4000);
  };

  const body = (
    <>
      {flash && (
        <p className="mt-3 flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-2 text-[13.5px] font-bold text-emerald-800">
          <Check className="h-4 w-4" strokeWidth={3} /> {flash}
        </p>
      )}

      {isLoading ? (
        <p className="mt-3 rounded-xl border border-hairline bg-white py-6 text-center text-sm text-slate-400">Loading…</p>
      ) : tracked === 0 ? (
        <div className="mt-3 rounded-xl border border-dashed border-hairline bg-slate-50/60 px-4 py-4 text-center">
          <p className="text-[13px] text-slate-400">No shelf items tracked yet.</p>
          {!picking && (
            <button type="button" onClick={() => setPicking(true)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2.5 text-[14px] font-bold text-white shadow-sm hover:bg-slate-800">
              <Plus className="h-4 w-4" strokeWidth={3} /> Track an Item
            </button>
          )}
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {due.map((d) => {
            const bad = (vals[d.id] ?? "").trim() !== "" && parsed(d) == null;
            return (
              <li key={d.id} className="flex items-center gap-3 rounded-2xl border border-hairline bg-white p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold leading-snug text-slate-900">{d.description ?? d.part_number}</p>
                  <p className="mt-0.5 text-[12.5px] text-slate-500">
                    {d.part_number}
                    {" · "}
                    {d.last_counted_at
                      ? <>had {d.qty_in_stock != null ? qtyText(Number(d.qty_in_stock)) : "?"}{d.units ? ` ${d.units}` : ""} · {dayText(d.last_counted_at)}</>
                      : <span className="font-semibold text-amber-700">first count</span>}
                  </p>
                </div>
                <input
                  value={vals[d.id] ?? ""}
                  onChange={(e) => setVals((v) => ({ ...v, [d.id]: e.target.value }))}
                  inputMode="decimal"
                  placeholder="#"
                  aria-label={`Count for ${d.part_number}`}
                  className={cn(
                    "w-20 shrink-0 rounded-xl border py-2.5 text-center text-[17px] font-extrabold tabular-nums focus:outline-none focus:ring-2",
                    bad ? "border-rose-300 focus:ring-rose-400/40" : "border-hairline focus:border-brand-500 focus:ring-brand-500/30",
                  )}
                />
                <button type="button"
                  title={arm === d.id ? "Tap again to stop counting this item" : "Stop counting this item"}
                  onClick={() => {
                    if (arm === d.id) { track.mutate({ id: d.id, tracked: false }); setArm(null); }
                    else setArm(d.id);
                  }}
                  className={cn(
                    "grid h-8 shrink-0 place-items-center rounded-lg text-[12px] font-bold",
                    arm === d.id ? "w-auto bg-rose-600 px-2 text-white" : "w-8 text-slate-300 hover:bg-slate-100 hover:text-slate-500",
                  )}>
                  {arm === d.id ? "Sure?" : <X className="h-4 w-4" strokeWidth={2.5} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {tracked > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-hairline pt-3">
          <button type="button" disabled={filled.length === 0 || save.isPending} onClick={() => void saveAll()}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-[14px] font-bold text-white shadow-sm hover:bg-slate-800 disabled:opacity-40">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {filled.length > 0 ? `Save ${filled.length}` : "Save"}
          </button>
          <span className="text-[12.5px] text-slate-400">tracking {tracked}</span>
          {!picking && (
            <button type="button" onClick={() => setPicking(true)}
              className="ml-auto inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[13px] font-bold text-brand-700 hover:bg-brand-50">
              <Plus className="h-3.5 w-3.5" strokeWidth={3} /> Track an Item
            </button>
          )}
        </div>
      )}

      {picking && (
        <div className="mt-3 rounded-xl border border-hairline bg-slate-50 p-3">
          <PricebookPicker
            placeholder="Search the price book…"
            pickLabel="Track"
            autoFocus
            onPick={(it) => {
              track.mutate({ id: it.id, tracked: true });
              setFlash(`Tracking ${it.part_number}.`);
              window.setTimeout(() => setFlash(null), 4000);
            }}
          />
          <button type="button" onClick={() => setPicking(false)}
            className="mt-2 rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-slate-500 hover:bg-slate-100">
            Done
          </button>
        </div>
      )}
    </>
  );

  if (embedded) return <div>{body}</div>;
  return (
    <section className="rounded-2xl border border-hairline bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-5 w-5 text-brand-600" />
        <h2 className="text-[17px] font-bold text-slate-900">Count a Few</h2>
        {tracked > 0 && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[13px] font-bold text-slate-600">{tracked}</span>
        )}
      </div>
      {body}
    </section>
  );
}
